/**
 * One call, whole, on top of the list rather than wedged underneath its own row.
 *
 * IT USED TO EXPAND IN PLACE, and three things were wrong with that at once: a long call
 * shoved every row below it off the screen, the lead card arrived as a `JSON.stringify`
 * dump in a half-width `<pre>`, and the row grid it hung off could not shrink, so on a
 * narrow console the columns ran into each other. A modal fixes the first by construction
 * and gives the other two the room to be read.
 *
 * THE CALL IS A PROP AND NEVER STATE. `LivePage` re-fetches the calls every fifteen seconds
 * and replaces the array wholesale, so it passes the freshly-found record down on every
 * render; anything held here would be stale inside the minute.
 *
 * THE LEAD CARD IS RENDERED BY SHAPE, NOT BY SCHEMA. `caller` and `captured` are whatever
 * the pod decided to take down - arbitrary keys, arbitrary values - so `show` below flattens
 * a value to one readable line and anything that flattens to nothing is dropped, rather than
 * arriving on screen as `{}` or `[object Object]`.
 */

import { Fragment, useState } from "react";
import { RecordingPlayer } from "./RecordingPlayer";
import type { CallRecord } from "./types";
import { CardHead, Modal, duration, when } from "./ui";

export function CallModal({ call, demoId, autoStart, onClose }: {
  call: CallRecord;
  demoId: string;
  /** The press that opened this was a press of play, so the transport starts itself. */
  autoStart: boolean;
  onClose: () => void;
}) {
  const [playing, setPlaying] = useState(false);
  const card = call.lead_card;
  const titleId = `call-${call.id}-title`;

  // The same sentence the row says, so the modal reads as that row opened rather than as a
  // second account of the call.
  const which = call.direction === "outbound"
    ? <>Rang them{call.situation ? <span className="text-muted"> · {call.situation.label}</span> : null}</>
    : <>Rang in{call.lane ? <span className="text-muted"> · {call.lane.replace(/_/g, " ")}</span> : null}</>;

  // A language is worth pointing at only where it CHANGES - the demo follows a caller into
  // another language, and that turn is the interesting one. Against the call's first stated
  // language, not against a hard-coded "en": nothing in the data says English is the floor.
  const opening = call.transcript.find((turn) => turn.language)?.language ?? null;

  const notes = card?.notes?.filter((note) => note.trim()) ?? [];
  // The card's own rules are sixty dashes wide, for a terminal this pane is not. They say
  // nothing, they cannot fit, and dropped they cost the card nothing but two wrapped lines.
  const written = (card?.text ?? "")
    .split("\n")
    .filter((line) => !/^[\s\-=_*·—–]+$/.test(line) || line.trim() === "")
    .join("\n")
    .trim();
  const bare = !written && !notes.length
    && !Object.values(card?.caller ?? {}).some((v) => show(v))
    && !Object.values(card?.captured ?? {}).some((v) => show(v));

  return (
    <Modal onClose={onClose} labelledBy={titleId}>
      <CardHead
        title={
          <span id={titleId} className="flex items-center gap-2 flex-wrap">
            {when(call.started_at)}
            <span className="text-faint">·</span>{which}
            <span className="text-faint">·</span><span className="text-muted font-normal">{duration(call.duration_ms)}</span>
          </span>
        }
        right={
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="-mr-1 shrink-0 w-7 h-7 inline-flex items-center justify-center rounded-md text-[18px] leading-none text-muted hover:text-ok"
          >
            ×
          </button>
        }
      />

      <div className="shrink-0 px-5 py-3.5 border-b border-line-soft">
        {call.recording ? (
          <RecordingPlayer
            path={`/v1/demos/${demoId}/calls/${call.call_id}/recording?v=${call.recording.sha256}`}
            durationMs={call.recording.durationMs || call.duration_ms || 0}
            playing={playing}
            onPlay={() => setPlaying(true)}
            onStop={() => setPlaying(false)}
            autoStart={autoStart}
          />
        ) : (
          <div className="text-[13px] text-muted">No recording for this call.</div>
        )}
      </div>

      {/* Two panes that SHRINK rather than push. From `md` up each scrolls on its own, so the
          header and the transport stay put however long the call ran; below it they stack and
          the body is one scroll, which is the only thing that reads on a phone. */}
      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_300px] grow min-h-0 overflow-y-auto md:overflow-hidden">
        <div className="min-h-0 md:overflow-y-auto px-5 py-4 flex flex-col gap-3">
          {call.transcript.map((turn, i) => {
            // Every field is optional and the ingest route validates the turns as `unknown`,
            // so nothing here may assume a shape the server never promised.
            const role = (turn.role ?? "").trim();
            const agent = role.toLowerCase() === "agent";
            const said = turn.text?.trim();
            const switched = !!turn.language && !!opening && turn.language !== opening;
            return (
              <div key={i} className="grid grid-cols-[58px_minmax(0,1fr)] gap-3 items-baseline">
                <span className={`text-[11px] uppercase tracking-[0.06em] truncate ${agent ? "text-ok" : "text-muted"}`} title={role || undefined}>
                  {role || "?"}
                </span>
                <span className={`text-[13px] leading-relaxed ${agent ? "text-ink-soft" : "text-ink"}`}>
                  {said || <span className="text-faint">(nothing said)</span>}
                  {switched && (
                    <span className="ml-2 px-1.5 py-0.5 bg-chip text-muted rounded text-[11px] uppercase tracking-[0.06em] align-middle">{turn.language}</span>
                  )}
                </span>
              </div>
            );
          })}
          {call.transcript.length === 0 && <span className="text-[13px] text-muted">No transcript.</span>}
        </div>

        <aside className="min-h-0 md:overflow-y-auto flex flex-col gap-4 px-5 py-4 bg-paper-soft border-t md:border-t-0 md:border-l border-line-soft">
          {written && (
            /* MONOSPACED AND `pre-wrap`, BECAUSE THE POD BUILT IT THAT WAY. `lead.py` writes
               this card in fixed-width columns - "About" then thirteen spaces then the value
               - so a proportional font with `pre-line` collapses the gap and "Notes" and
               "Reached Sam." become one run-on sentence. Preserving the spaces is the only
               rendering of this string that is not a misquote of it. */
            <div className="px-3.5 py-3 bg-paper border border-line-soft rounded-lg font-mono text-[11px] leading-relaxed whitespace-pre-wrap break-words">{written}</div>
          )}
          <Facts title="Caller" bag={card?.caller} />
          <Facts title="Captured" bag={card?.captured} />
          {notes.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <div className="text-[12px] text-muted">Notes</div>
              {notes.map((note, i) => (
                <div key={i} className="flex gap-2 text-[13px] leading-relaxed"><span className="text-faint">·</span><span className="break-words">{note}</span></div>
              ))}
            </div>
          )}
          {bare && <div className="text-[13px] text-muted">Nothing was taken down.</div>}
          {call.end_reason && <div className="text-[12px] text-muted mt-auto pt-2">Ended: {call.end_reason}</div>}
        </aside>
      </div>
    </Modal>
  );
}

/** One of the lead card's bags, as label/value rows. Nothing at all when it is empty. */
function Facts({ title, bag }: { title: string; bag?: Record<string, unknown> }) {
  const rows = Object.entries(bag ?? {})
    .map(([key, value]) => [key, show(value)] as const)
    .filter(([, value]) => value !== "");
  if (rows.length === 0) return null;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="text-[12px] text-muted">{title}</div>
      <div className="grid grid-cols-[88px_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-[13px] leading-snug">
        {rows.map(([key, value]) => (
          <Fragment key={key}>
            <span className="text-[12px] text-muted break-words">{label(key)}</span>
            <span className="break-words">{value}</span>
          </Fragment>
        ))}
      </div>
    </div>
  );
}

/** `first_name` / `firstName` -> "First name". */
function label(key: string): string {
  const words = key.replace(/[_-]+/g, " ").replace(/([a-z\d])([A-Z])/g, "$1 $2").trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** One readable line out of whatever the pod put there, or "" for anything that has none. */
function show(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : "";
  if (typeof value === "string") return value.trim();
  if (Array.isArray(value)) return value.map(show).filter(Boolean).join(", ");
  if (typeof value === "object") {
    return Object.entries(value)
      .map(([key, inner]) => (show(inner) ? `${label(key)}: ${show(inner)}` : ""))
      .filter(Boolean)
      .join(" · ");
  }
  return "";
}
